import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { safeLocalStorage } from "@/utils/safeStorage";

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸", rtl: false },
  { code: "es", name: "Spanish", flag: "🇪🇸", rtl: false },
  { code: "fr", name: "French", flag: "🇫🇷", rtl: false },
  { code: "de", name: "German", flag: "🇩🇪", rtl: false },
  { code: "it", name: "Italian", flag: "🇮🇹", rtl: false },
  { code: "pt", name: "Portuguese", flag: "🇧🇷", rtl: false },
  { code: "ru", name: "Russian", flag: "🇷🇺", rtl: false },
  { code: "ja", name: "Japanese", flag: "🇯🇵", rtl: false },
  { code: "ko", name: "Korean", flag: "🇰🇷", rtl: false },
  { code: "zh", name: "Chinese", flag: "🇨🇳", rtl: false },
  { code: "ar", name: "Arabic", flag: "🇸🇦", rtl: true },
  { code: "hi", name: "Hindi", flag: "🇮🇳", rtl: false },
  { code: "nl", name: "Dutch", flag: "🇳🇱", rtl: false },
  { code: "pl", name: "Polish", flag: "🇵🇱", rtl: false },
  { code: "tr", name: "Turkish", flag: "🇹🇷", rtl: false },
  { code: "vi", name: "Vietnamese", flag: "🇻🇳", rtl: false },
  { code: "th", name: "Thai", flag: "🇹🇭", rtl: false },
  { code: "id", name: "Indonesian", flag: "🇮🇩", rtl: false },
  { code: "sv", name: "Swedish", flag: "🇸🇪", rtl: false },
  { code: "uk", name: "Ukrainian", flag: "🇺🇦", rtl: false },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

interface LanguageContextType {
  language: LanguageCode;
  languageName: string;
  setLanguage: (code: LanguageCode) => void;
  isEnglish: boolean;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  languageName: "English",
  setLanguage: () => {},
  isEnglish: true,
  isRTL: false,
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const saved = safeLocalStorage.getItem("gb-language") as LanguageCode | null;
    return SUPPORTED_LANGUAGES.some((item) => item.code === saved) ? saved : "en";
  });

  const langInfo = SUPPORTED_LANGUAGES.find((l) => l.code === language) || SUPPORTED_LANGUAGES[0];

  const setLanguage = useCallback((code: LanguageCode) => {
    setLanguageState(code);
    safeLocalStorage.setItem("gb-language", code);
  }, []);

  // RTL support
  useEffect(() => {
    const html = document.documentElement;
    if (langInfo.rtl) {
      html.setAttribute("dir", "rtl");
    } else {
      html.setAttribute("dir", "ltr");
    }
  }, [langInfo.rtl]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        languageName: langInfo.name,
        setLanguage,
        isEnglish: language === "en",
        isRTL: langInfo.rtl,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
